import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CourseCard } from "@/components/site/CourseCard";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function Store() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [level, setLevel] = useState(params.get("level") || "all");
  const [priceFilter, setPriceFilter] = useState(params.get("free") === "true" ? "free" : "all");
  const [sort, setSort] = useState(params.get("sort") || "order");

  useEffect(() => { track("page_view", { page: "store" }); }, []);

  const { data: cats } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/catalog/categories")).data });

  const { data, isLoading } = useQuery({
    queryKey: ["courses", q, category, level, priceFilter, sort],
    queryFn: async () => {
      const p = { sort };
      if (q) p.q = q;
      if (category !== "all") p.category = category;
      if (level !== "all") p.skill_level = level;
      if (priceFilter === "free") p.is_free = "true";
      if (priceFilter === "paid") p.is_free = "false";
      return (await api.get("/catalog/courses", { params: p })).data;
    },
  });

  const items = data?.items || [];

  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="font-display text-4xl font-bold uppercase tracking-tighter sm:text-5xl">Course Store</h1>
        <p className="mt-2 text-zinc-400">Premium barber training. Buy full courses or individual videos.</p>

        <div className="mt-8 grid gap-4 rounded-lg border border-white/10 bg-zinc-900/40 p-4 md:grid-cols-[1fr_auto_auto_auto_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="store-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses..."
              className="bg-zinc-950 border-white/10 pl-9 text-white" />
          </div>
          <FilterSelect testid="filter-category" value={category} onChange={setCategory} placeholder="Category"
            options={[{ v: "all", l: "All Categories" }, ...(cats || []).map((c) => ({ v: c.id, l: c.name }))]} />
          <FilterSelect testid="filter-level" value={level} onChange={setLevel} placeholder="Level"
            options={[{ v: "all", l: "All Levels" }, ...LEVELS.map((l) => ({ v: l, l }))]} />
          <FilterSelect testid="filter-price" value={priceFilter} onChange={setPriceFilter} placeholder="Price"
            options={[{ v: "all", l: "Free & Paid" }, { v: "free", l: "Free only" }, { v: "paid", l: "Paid only" }]} />
          <FilterSelect testid="filter-sort" value={sort} onChange={setSort} placeholder="Sort"
            options={[{ v: "order", l: "Featured" }, { v: "newest", l: "Newest" }, { v: "popular", l: "Popular" }, { v: "price_low", l: "Price ↑" }, { v: "price_high", l: "Price ↓" }]} />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" /></div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center text-zinc-500">
            <SlidersHorizontal className="mx-auto mb-4 h-10 w-10" />
            No courses match your filters yet.
          </div>
        ) : (
          <div data-testid="store-grid" className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((c) => <CourseCard key={c.id} course={c} />)}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="min-w-[150px] bg-zinc-950 border-white/10 text-white">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-white/10 text-white">
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
